import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Appointment } from '@shared/types/index.ts';
import { scheduleApi } from '../api/endpoints.ts';
import type { SeriesAppointmentsPage } from '../api/endpoints.ts';
import { ApiRequestError } from '../api/client.ts';
import { useOnlineStatus } from './index.ts';

/** Additional pages belong to the exact first page and session that requested them. */
export function useSeriesVisits({
  seriesId,
  initial,
  pagination,
  source,
  refreshing,
  enabled,
}: {
  seriesId: string;
  initial: Appointment[];
  pagination?: Omit<SeriesAppointmentsPage, 'items'>;
  source: string;
  refreshing: boolean;
  enabled: boolean;
}) {
  const { t } = useTranslation();
  const online = useOnlineStatus();
  const [extra, setExtra] = useState<{ source: string; items: Appointment[]; page: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsRefresh, setNeedsRefresh] = useState(false);
  const request = useRef(0);
  const active = useRef<AbortController | null>(null);
  const busy = useRef(false);
  const latest = useRef({ source, refreshing, enabled, online });
  latest.current = { source, refreshing, enabled, online };
  const current = extra?.source === source ? extra : null;
  const items = [...initial, ...(current?.items ?? [])];
  const currentPage = current?.page ?? pagination?.page ?? 1;
  const snapshotSupported = Boolean(pagination?.snapshotAt && pagination.snapshotToken);
  const paginationUnavailable = Boolean((pagination && !snapshotSupported) || (!pagination && initial.length >= 100));
  const hasMore = Boolean(snapshotSupported && pagination && currentPage < pagination.totalPages);

  useEffect(() => {
    request.current++;
    active.current?.abort();
    active.current = null;
    busy.current = false;
    setLoading(false);
    setExtra(null);
    setError(null);
    setNeedsRefresh(false);
    return () => {
      request.current++;
      active.current?.abort();
    };
  }, [source, enabled]);

  async function loadMore() {
    if (
      busy.current ||
      !enabled ||
      refreshing ||
      !online ||
      !hasMore ||
      !pagination?.snapshotAt ||
      !pagination.snapshotToken ||
      needsRefresh
    )
      return;
    busy.current = true;
    setLoading(true);
    setError(null);
    const expectedSource = source;
    const expectedPage = currentPage + 1;
    const generation = ++request.current;
    const controller = new AbortController();
    active.current = controller;
    const isCurrent = () =>
      generation === request.current && latest.current.source === expectedSource && latest.current.enabled;
    try {
      const result = await scheduleApi.seriesAppointments(
        seriesId,
        {
          page: expectedPage,
          pageSize: pagination.pageSize,
          snapshotAt: pagination.snapshotAt,
          snapshotToken: pagination.snapshotToken,
        },
        controller.signal,
      );
      if (!isCurrent() || latest.current.refreshing) return;
      const known = new Set(items.map((item) => item.id));
      const fresh = result.items.filter((item) => !known.has(item.id));
      if (
        result.page !== expectedPage ||
        result.pageSize !== pagination.pageSize ||
        result.total !== pagination.total ||
        result.totalPages !== pagination.totalPages ||
        result.snapshotAt !== pagination.snapshotAt ||
        result.snapshotToken !== pagination.snapshotToken ||
        new Set(result.items.map((item) => item.id)).size !== result.items.length ||
        fresh.length !== result.items.length ||
        (result.page === result.totalPages && items.length + fresh.length !== result.total)
      ) {
        setNeedsRefresh(true);
        setError(t('seriesDetail.visitsChanged'));
        return;
      }
      setExtra({ source: expectedSource, items: [...(current?.items ?? []), ...fresh], page: expectedPage });
    } catch (caught) {
      if (isCurrent() && !controller.signal.aborted) {
        if (caught instanceof ApiRequestError && caught.status === 409) {
          setNeedsRefresh(true);
          setError(t('seriesDetail.visitsChanged'));
        } else {
          setError(caught instanceof ApiRequestError ? caught.message : t('seriesDetail.loadMoreError'));
        }
      }
    } finally {
      if (isCurrent()) {
        busy.current = false;
        active.current = null;
        setLoading(false);
      }
    }
  }

  return {
    items,
    hasMore,
    loading,
    error,
    needsRefresh,
    online,
    snapshotSupported,
    paginationUnavailable,
    loadMore: () => void loadMore(),
  };
}
